import React, { useState } from 'react';
import { products } from '../data/mockData';
import { CheckCircle, Filter } from 'lucide-react';

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(products.map(p => p.category))];

  const filteredProducts = selectedCategory === 'All'
    ? products
    : products.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen pt-24 bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-[#004534] to-[#0C6951] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="solar-grid-bg w-full h-full"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Our Solar Products
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Premium quality solar solutions with industry-leading warranties and performance
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white shadow-md top-20 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center space-x-2 flex-wrap gap-3">
            <Filter className="w-5 h-5 text-gray-600" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-[#22c55e] text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-[#22c55e] text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    {product.category}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-bold text-[#004534] mb-3 group-hover:text-[#0C6951] transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {product.description}
                  </p>

                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Key Features:</p>
                    <div className="space-y-2">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    <p className="text-sm text-gray-500 font-medium">
                      <span className="text-[#004534] font-bold">Capacity:</span> {product.capacity}
                    </p>
                  </div>

                  <button className="mt-6 w-full bg-[#22c55e] hover:bg-[#16a34a] text-white py-3 rounded-full font-semibold transition-all duration-300 hover:scale-105 shadow-md">
                    Get Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Products Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#004534] mb-4">
              Why Choose Our Products?
            </h2>
            <p className="text-xl text-gray-600">
              Quality, reliability, and performance guaranteed
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                title: 'Premium Quality',
                description: 'Only top-tier components from trusted manufacturers',
              },
              {
                title: 'Long Warranties',
                description: 'Up to 25 years warranty on solar panels',
              },
              {
                title: 'High Efficiency',
                description: 'Maximum power generation with latest technology',
              },
              {
                title: 'Expert Installation',
                description: 'Professional installation by certified technicians',
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="bg-gradient-to-br from-[#1e3a8a] to-[#22c55e] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#004534] mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style jsx>{`
        .solar-grid-bg {
          background-image: 
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px);
          background-size: 80px 80px;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default Products;
